/**
 * useImperativeSession — Imperative BA session fetching
 *
 * Extracted from AuthContext.tsx. Uses authClient.getSession() instead of
 * the nanostores-backed useSession() hook, which returns { data: null }
 * on Expo web due to BA's $fetch credential handling / nanostores hydration race.
 */
import { useState, useRef, useCallback, useEffect } from "react";
import * as Sentry from "@sentry/react-native";
import { authClient } from "../lib/better-auth-client";

/**
 * B-lite type: only the fields AuthContext consumes.
 * `image` allows undefined because BA's getSession() returns
 * `image?: string | null | undefined`.
 */
export interface BaSession {
  session: { token: string };
  user: {
    id: string;
    email: string;
    name: string;
    image?: string | null;
    emailVerified: boolean;
    createdAt: Date;
    /**
     * True when the user record was created via BA's anonymous plugin.
     * Phase 2 (NocoDB #3843 / EPIC #3840): distinguishes anon BA sessions from
     * real authenticated sessions so clients can gate non-applicable queries
     * and keep auth status='anonymous' (preventing the signOut cascade on 401).
     */
    isAnonymous?: boolean;
  };
}

interface UseImperativeSessionResult {
  baSession: BaSession | null;
  sessionLoading: boolean;
  refreshSession: () => Promise<void>;
  clearSession: () => void;
}

export function useImperativeSession(): UseImperativeSessionResult {
  const [baSession, setBaSession] = useState<BaSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const sessionFetchRef = useRef<Promise<void> | null>(null);

  // Single-flight mount-time session fetch.
  // BA's $fetch sends cookies automatically — run once on mount.
  useEffect(() => {
    // Single-flight: if already fetching, don't start another
    if (sessionFetchRef.current) return;

    const fetchSession = async () => {
      Sentry.addBreadcrumb({ category: 'auth.session', message: 'Session fetch started (mount)', level: 'info' });
      try {
        const result = await authClient.getSession({
          fetchOptions: { credentials: 'include' },
        });
        if (result.data) {
          Sentry.addBreadcrumb({ category: 'auth.session', message: 'Session fetch success', level: 'info', data: { email: result.data.user?.email?.slice(0, 3) + '***' } });
          setBaSession(result.data);
        } else if (result.error) {
          Sentry.addBreadcrumb({ category: 'auth.session', message: 'Session fetch error', level: 'warning', data: { error: result.error.message } });
          console.warn('[useImperativeSession] getSession error:', result.error.message);
        }
      } catch (err) {
        Sentry.addBreadcrumb({ category: 'auth.session', message: 'Session fetch exception', level: 'error', data: { error: String(err) } });
        console.error('[useImperativeSession] getSession failed:', err);
      } finally {
        setSessionLoading(false);
        sessionFetchRef.current = null;
      }
    };

    sessionFetchRef.current = fetchSession();
  }, []); // Run once on mount

  // Helper: re-fetch session after sign-in/sign-up
  const refreshSession = useCallback(async () => {
    Sentry.addBreadcrumb({ category: 'auth.session', message: 'refreshSession called', level: 'info' });
    const result = await authClient.getSession({
      fetchOptions: { credentials: 'include' },
    });
    if (result.data) {
      Sentry.addBreadcrumb({ category: 'auth.session', message: 'refreshSession success', level: 'info', data: { email: result.data.user?.email?.slice(0, 3) + '***' } });
      setBaSession(result.data);
    } else if (result.error) {
      Sentry.addBreadcrumb({ category: 'auth.session', message: 'refreshSession error', level: 'error', data: { error: result.error.message } });
      console.error('[useImperativeSession] refreshSession error:', result.error.message);
      throw new Error(result.error.message);
    }
  }, []);

  const clearSession = useCallback(() => {
    Sentry.addBreadcrumb({ category: 'auth.session', message: 'clearSession called', level: 'info' });
    setBaSession(null);
  }, []);

  return { baSession, sessionLoading, refreshSession, clearSession };
}
